// src/admin/pages/Dashboard/ProcessesPage.js

import React, { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";

// Componenti
import ProcessFilterBar from "./components/process/ProcessFilterBar";
import ProcessTable from "./components/process/ProcessTable";
import ProcessChartSection from "./components/process/ProcessChartSection";

export default function ProcessesPage() {
  // ------------------------------------------------
  // 1) Stati per Filtri
  // ------------------------------------------------
  const [startDate, setStartDate] = useState(
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(
    format(endOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [restaurantId, setRestaurantId] = useState("all");

  // ------------------------------------------------
  // 2) Stato per dayOfWeek (fasce orarie)
  // ------------------------------------------------
  // "all" => niente filtro
  // "0..6" => giorno della settimana
  const [hourlyDayOfWeek, setHourlyDayOfWeek] = useState("all");

  // ------------------------------------------------
  // 3) Stati per i dati
  // ------------------------------------------------
  const [restaurants, setRestaurants] = useState([]);

  // Raw data
  const [rawData, setRawData] = useState([]);

  // Chart data (vari endpoint)
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [dishMetrics, setDishMetrics] = useState([]);
  const [weeklyMetrics, setWeeklyMetrics] = useState([]);
  const [monthlyMetrics, setMonthlyMetrics] = useState([]);
  const [yearlyMetrics, setYearlyMetrics] = useState([]);

  // Hourly data
  const [hourlyMetrics, setHourlyMetrics] = useState([]);

  // ------------------------------------------------
  // 4) Stati di UI
  // ------------------------------------------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState("raw"); // "raw" o "chart"

  const API_BASE_URL = `${process.env.REACT_APP_API_URL}`; // Adatta al tuo server

  // ------------------------------------------------
  // 5) FUNZIONI DI FETCH
  // ------------------------------------------------

  // (A) Carica la lista ristoranti
  async function fetchRestaurants() {
    try {
      const token = localStorage.getItem("access_token") || "";
      const url = `${API_BASE_URL}/admin/kpi/restaurants_list`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore ristoranti: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setRestaurants(data);
    } catch (err) {
      console.error("fetchRestaurants error:", err);
    }
  }

  // (B) Carica i raw data
  async function fetchRawData() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      if (!token) {
        throw new Error("Token mancante, effettua login Admin.");
      }

      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/raw_data${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore raw_data: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setRawData(data);
    } catch (err) {
      console.error("fetchRawData error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (C) Carica i daily metrics
  async function fetchDailyMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/daily_metrics${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore daily_metrics: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setDailyMetrics(data);
    } catch (err) {
      console.error("fetchDailyMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (D) Carica i dish metrics
  async function fetchDishMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/dish_metrics${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore dish_metrics: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setDishMetrics(data);
    } catch (err) {
      console.error("fetchDishMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (E) Carica i weekly metrics
  async function fetchWeeklyMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/weekly_metrics${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore weekly_metrics: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setWeeklyMetrics(data);
    } catch (err) {
      console.error("fetchWeeklyMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (F) Carica i monthly metrics
  async function fetchMonthlyMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/monthly_metrics${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore monthly_metrics: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setMonthlyMetrics(data);
    } catch (err) {
      console.error("fetchMonthlyMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (G) Carica i yearly metrics
  async function fetchYearlyMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      const query = params.length ? `?${params.join("&")}` : "";

      const url = `${API_BASE_URL}/admin/processes/yearly_metrics${query}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Errore yearly_metrics: ${res.status} - ${errText}`);
      }
      const data = await res.json();
      setYearlyMetrics(data);
    } catch (err) {
      console.error("fetchYearlyMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // (H) Carica gli hourly metrics, usando hourlyDayOfWeek
  async function fetchHourlyMetrics() {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token") || "";
      const params = [];
      if (restaurantId !== "all") params.push(`restaurant_id=${restaurantId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);

      // Se hourlyDayOfWeek != "all", aggiungi day_of_week
      if (hourlyDayOfWeek !== "all") {
        params.push(`day_of_week=${hourlyDayOfWeek}`);
      }

      const query = params.length ? "?" + params.join("&") : "";
      const url = `${API_BASE_URL}/admin/processes/hourly_metrics${query}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errTxt = await res.text();
        throw new Error(`Errore hourly_metrics: ${res.status} - ${errTxt}`);
      }
      const data = await res.json();
      setHourlyMetrics(data);
    } catch (err) {
      console.error("fetchHourlyMetrics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ------------------------------------------------
  // 6) useEffect iniziale => carichiamo tutto
  // ------------------------------------------------
  useEffect(() => {
    fetchRestaurants();
    fetchRawData();
    fetchDailyMetrics();
    fetchDishMetrics();
    fetchWeeklyMetrics();
    fetchMonthlyMetrics();
    fetchYearlyMetrics();
    // Faccio una prima fetch oraria con dayOfWeek=all
    fetchHourlyMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------
  // 7) useEffect su hourlyDayOfWeek => aggiorna le fasce orarie
  // ------------------------------------------------
  useEffect(() => {
    // Ogni volta che hourlyDayOfWeek cambia (es. da "3" a "all"),
    // rifacciamo la fetch => dati coerenti
    fetchHourlyMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourlyDayOfWeek]);

  // ------------------------------------------------
  // 8) handleFilter => ricarica tutto al click
  // ------------------------------------------------
  const handleFilter = () => {
    fetchRawData();
    fetchDailyMetrics();
    fetchDishMetrics();
    fetchWeeklyMetrics();
    fetchMonthlyMetrics();
    fetchYearlyMetrics();
    // Ricarichiamo hourly
    fetchHourlyMetrics();
  };

  // Callback per dayOfWeek => cambia lo stato
  const handleDayOfWeekChange = (val) => {
    setHourlyDayOfWeek(val);
    // Non serve fetchHourlyMetrics() qui: 
    // ci pensa lo useEffect([hourlyDayOfWeek])
  };

  // ------------------------------------------------
  // RENDER UI
  // ------------------------------------------------
  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Processi - Analisi</h2>

      <ProcessFilterBar
        startDate={startDate}
        endDate={endDate}
        onChangeStart={setStartDate}
        onChangeEnd={setEndDate}
        restaurants={restaurants}
        selectedRestaurant={restaurantId}
        onChangeRestaurant={setRestaurantId}
        onFilter={handleFilter}
      />

      <div style={styles.tabContainer}>
        <button
          onClick={() => setCurrentTab("raw")}
          style={{
            ...styles.tabButton,
            backgroundColor: currentTab === "raw" ? "#FF6600" : "#ccc",
            color: currentTab === "raw" ? "#fff" : "#333",
          }}
        >
          Raw Data
        </button>
        <button
          onClick={() => setCurrentTab("chart")}
          style={{
            ...styles.tabButton,
            backgroundColor: currentTab === "chart" ? "#FF6600" : "#ccc",
            color: currentTab === "chart" ? "#fff" : "#333",
          }}
        >
          Charts
        </button>
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}
      {loading && <p>Caricamento in corso...</p>}

      {currentTab === "raw" && (
        <ProcessTable rawData={rawData} />
      )}

      {currentTab === "chart" && (
        <ProcessChartSection
          dailyMetrics={dailyMetrics}
          dishMetrics={dishMetrics}
          weeklyMetrics={weeklyMetrics}
          monthlyMetrics={monthlyMetrics}
          yearlyMetrics={yearlyMetrics}
          // Passiamo i dati hourly
          hourlyMetrics={hourlyMetrics}
          // Passiamo dayOfWeek e callback
          dayOfWeek={hourlyDayOfWeek}
          onChangeDayOfWeek={handleDayOfWeekChange}
        />
      )}
    </div>
  );
}

// ------------------------------------------------
// Stili di base
// ------------------------------------------------
const styles = {
  page: {
    backgroundColor: "#fefefe",
    minHeight: "100vh",
    padding: "1rem",
  },
  title: {
    marginBottom: "1rem",
    color: "#FF6600",
  },
  tabContainer: {
    display: "flex",
    gap: "1rem",
    margin: "1rem 0",
  },
  tabButton: {
    border: "none",
    borderRadius: "6px",
    padding: "0.6rem 1rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
};
